import {
  Check,
  Download,
  EyeOff,
  ImageIcon,
  Play,
  ShieldX,
  Sparkles,
  StarOff,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { formatBytes, formatDate, statusClass, statusLabel } from "../../lib/format";
import type { MediaItem } from "../../lib/types";

type Action = "approve" | "not-approve" | "hide" | "feature" | "unfeature" | "delete";

interface MediaCardProps {
  item: MediaItem;
  selectable?: boolean;
  selected?: boolean;
  onSelect?: (selected: boolean) => void;
  onAction?: (id: string, action: Action) => Promise<void> | void;
}

export function MediaCard({ item, selectable, selected, onSelect, onAction }: MediaCardProps) {
  const [busy, setBusy] = useState<Action | null>(null);

  const runAction = async (action: Action) => {
    if (action === "delete" && !window.confirm("Move this media item to Deleted? Permanent removal requires a second confirmation later.")) {
      return;
    }
    setBusy(action);
    try {
      await onAction?.(item.id, action);
    } finally {
      setBusy(null);
    }
  };

  const preview = item.url ? (
    item.fileType === "photo" ? (
      <img src={item.url} alt={item.caption || item.fileName} className="h-full w-full object-cover" loading="lazy" />
    ) : (
      <video src={item.url} className="h-full w-full object-cover" controls preload="metadata" />
    )
  ) : (
    <div className="media-frame grid h-full place-items-center text-white">
      <div className="text-center">
        {item.fileType === "photo" ? <ImageIcon className="mx-auto size-10 text-ktd-softGold" /> : <Play className="mx-auto size-10 text-ktd-softGold" />}
        <p className="mt-3 text-sm font-bold">{item.fileType === "photo" ? "Photo Preview" : "Video Preview"}</p>
      </div>
    </div>
  );

  const buttons = [
    { label: "Approve", action: "approve" as Action, icon: Check },
    { label: "Do Not Approve", action: "not-approve" as Action, icon: ShieldX },
    { label: "Hide", action: "hide" as Action, icon: EyeOff },
    {
      label: item.featured ? "Remove Featured" : "Feature in Vault",
      action: item.featured ? ("unfeature" as Action) : ("feature" as Action),
      icon: item.featured ? StarOff : Sparkles,
    },
    { label: "Delete", action: "delete" as Action, icon: Trash2 },
  ];

  return (
    <article className="media-card ivory-card overflow-hidden rounded-lg">
      <div className="relative aspect-[4/3] overflow-hidden bg-ktd-rich">
        {selectable && (
          <label className="absolute left-3 top-3 z-10 rounded-lg bg-ktd-black/82 px-3 py-2 text-xs font-bold text-white backdrop-blur">
            <input
              type="checkbox"
              checked={Boolean(selected)}
              onChange={(event) => onSelect?.(event.target.checked)}
              className="mr-2 accent-ktd-gold"
            />
            Select
          </label>
        )}
        <span className={`status-pill absolute right-3 top-3 z-10 ${statusClass(item.status, item.featured)}`}>
          {item.featured ? "Featured" : statusLabel[item.status]}
        </span>
        {preview}
      </div>
      <div className="grid gap-4 p-5">
        <div>
          <h3 className="break-words text-lg font-extrabold text-ktd-rich">{item.fileName}</h3>
          <p className="mt-1 text-sm text-ktd-muted">
            {item.fileType.toUpperCase()} - {formatBytes(item.fileSize)} - Uploaded {formatDate(item.createdAt)}
          </p>
        </div>
        <dl className="grid gap-2 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="font-bold text-ktd-rich">Uploader</dt>
            <dd className="text-right text-ktd-muted">{item.uploaderName || "Guest"}</dd>
          </div>
          {(item.uploaderEmail || item.uploaderPhone) && (
            <div className="flex justify-between gap-4">
              <dt className="font-bold text-ktd-rich">Contact</dt>
              <dd className="break-words text-right text-ktd-muted">{item.uploaderEmail || item.uploaderPhone}</dd>
            </div>
          )}
          {item.relationship && (
            <div className="flex justify-between gap-4">
              <dt className="font-bold text-ktd-rich">Relationship</dt>
              <dd className="text-right text-ktd-muted">{item.relationship}</dd>
            </div>
          )}
          <div className="flex justify-between gap-4">
            <dt className="font-bold text-ktd-rich">Status</dt>
            <dd className="text-right text-ktd-muted">{statusLabel[item.status]}</dd>
          </div>
        </dl>
        {item.caption && <p className="rounded-lg border border-ktd-gold/10 bg-white/75 p-4 text-sm leading-6 text-ktd-muted">{item.caption}</p>}
        <div className="grid grid-cols-2 gap-2">
          {buttons.map((button) => {
            const Icon = button.icon;
            return (
              <button
                key={button.label}
                type="button"
                disabled={busy !== null}
                onClick={() => void runAction(button.action)}
                className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-ktd-gold/25 bg-white px-3 py-2 text-xs font-extrabold text-ktd-rich transition hover:border-ktd-gold hover:bg-ktd-black hover:text-ktd-softGold disabled:opacity-50"
              >
                <Icon className="size-4" aria-hidden="true" />
                {busy === button.action ? "Working" : button.label}
              </button>
            );
          })}
          <a
            href={item.downloadUrl || item.url || "#"}
            className="focus-ring col-span-2 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-ktd-black px-3 py-2 text-xs font-extrabold text-ktd-softGold transition hover:bg-ktd-gold hover:text-ktd-black"
          >
            <Download className="size-4" aria-hidden="true" />
            Download
          </a>
        </div>
      </div>
    </article>
  );
}
